import c3 from "c3";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";

interface C3ContentData {
    id: string;
    index: number;
    name: string;
    value: number;
    x: number;
}

abstract class C3Chart<TProps, TState> extends BaseReactComponent<TProps, TState> {
    private timeoutRef: number;

    protected chartApi: c3.ChartAPI;

    public componentDidMount(): void {
        this.debouncedRenderChart();
    }

    public componentDidUpdate(): void {
        this.debouncedRenderChart();
    }

    public componentWillUnmount(): void {
        this.clearComponentTimeOut();
        this.chartApi && this.chartApi.destroy();
    }

    public render(): JSX.Element {
        return (
            <div className="c3-chart" data-cy={`chart-${this.getControlId()}`}>
                <div id={"q" + this.getControlId()} className="hide-circles" />
                <div id={`legend_${this.getControlId()}`} />
            </div>
        );
    }

    protected renderGraph(): void {
        if (this.chartApi) {
            this.chartApi.destroy();
        }

        this.chartApi = c3.generate(this.getC3Config());

        $("#q" + this.getControlId()).append(this.chartApi.element); // Render Graph
    }

    protected abstract getControlId(): string;
    protected abstract getData(): c3.Data;
    protected abstract getAxis(): c3.Axis;

    protected onRendered(): void {
        this.clearComponentTimeOut();
    }

    protected getC3Config(): c3.ChartConfiguration {
        return {
            padding: {
                right: 10
            },
            size: {
                height: 518
            },
            data: this.getData(),
            zoom: { enabled: false },
            axis: this.removeAxisPadding(this.getAxis()),
            legend: {
                show: false
            },
            grid: this.getGrid(),
            tooltip: this.getTooltip(),
            onresized: () => this.chartApi.flush(),
            onrendered: () => this.onRendered(),
            regions: this.getRegions()
        };
    }

    protected getGrid(): c3.Grid {
        return {
            x: { show: true },
            y: { show: true }
        };
    }

    protected getTooltip(): c3.TooltipOptions {
        return {
            show: false
        };
    }

    protected getRegions(): c3.RegionOptions[] {
        return [];
    }

    private debouncedRenderChart(): void {
        this.clearComponentTimeOut();
        this.timeoutRef = window.setTimeout(() => {
            this.renderGraph();
        }, 250);
    }

    private clearComponentTimeOut(): void {
        if (this.timeoutRef !== -1) {
            clearTimeout(this.timeoutRef);
            this.timeoutRef = -1;
        }
    }

    private removeAxisPadding(axis: c3.Axis): c3.Axis {
        if (!axis.x || !axis.y)
            return axis;

        axis.x.padding = {
            left: 0,
            right: 0
        };
        axis.y.padding = {
            top: 0,
            bottom: 0
        };
        return axis;
    }
}

export { C3Chart, C3ContentData };
